import React, { FC, useState } from "react";
import { Icon, Button, Divider } from "semantic-ui-react";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Carousel as ReactCarousel } from "react-responsive-carousel";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../../../hooks";
import "./StudentMenu.scss";
import { UserSidebarForm } from "./UserSidebarForm";
import { SettingsSidebarForm } from "./SettingsSidebarForm";
import { PasswordUpdateForm } from "./PasswordUpdateForm/PasswordUpdateForm";

const tabs = ["bars", "user", "settings"];

type NavProps = {
    activeTab: number;
    onTabClicked: (tab: number) => void;
};

const Nav: FC<NavProps> = ({ activeTab, onTabClicked }) => (
    <header className="tabs-student-menu">
        {tabs.map((tab, index) => (
            <button
                key={tab}
                type="button"
                onClick={() => onTabClicked(index)}
                className={`tabs-student-menu__button ${activeTab === index ? "active" : ""}`}
            >
                <Icon size="large" name={tab as any} />
            </button>
        ))}
        <div
            className="underline"
            style={{
                translate: `${activeTab * 100}% 0`,
            }}
        />
    </header>
);

// --- Constantes de navegación ---
const navItems = [
    {
        name: "Inicio",
        icon: "home" as const,
        to: "/student/dashboard",
        path: "/student/dashboard"
    },
    {
        name: "Mis cursos",
        icon: "book" as const,
        to: "/student/myCourses",
        path: ["/student/myCourses", /^\/student\/myCourses\/\d+\/$/]
    },
    {
        name: "Foro académico",
        icon: "users" as const,
        to: "/student/forum",
        path: ["/student/forum", /^\/student\/post\/\d+\/$/]
    },
    {
        name: "Clases en vivo",
        icon: "video" as const,
        to: "/student/liveClasses",
        path: "/student/liveClasses"
    },
    {
        name: "Mis pagos",
        icon: "money bill alternate outline" as const,
        to: "/student/myPayments",
        path: "/student/myPayments"
    },
    {
        name: "Mis calificaciones",
        icon: "clipboard list" as const,
        to: "/student/myGrades",
        path: "/student/myGrades"
    },
    {
        name: "Mis notificaciones",
        icon: "history" as const,
        to: "/student/myNotifications",
        path: "/student/myNotifications"
    },
    {
        name: "Documentos institucionales",
        icon: "file pdf outline" as const,
        to: "/student/institutional-documents",
        path: "/student/institutional-documents"
    },
    {
        name: "Chat AI",
        icon: "chat" as const,
        to: "/student/aiChat",
        path: "/student/aiChat"
    }
];

// --- NavButton tipado y seguro ---
type ButtonProps = {
    onClick: (item: string) => void;
    name: string;
    to: string;
    icon?: string;
    isActive: boolean;
    hasSubNav?: boolean;
};

const NavButton: FC<ButtonProps> = ({
    onClick,
    name,
    icon,
    to,
    isActive,
    hasSubNav,
}) => (
    <Link to={to}>
        <Button
            type="button"
            onClick={() => onClick(name)}
            className={`${isActive ? "active" : ""} ${hasSubNav ? "has-sub-nav" : ""}`}
        >
            {icon && <Icon name={icon as any} />}
            <span>{name}</span>
            {hasSubNav && <Icon name="chevron down" />}
        </Button>
    </Link>
);

export const StudentMenu = (props) => {
    const { sidebarOpen, darkLightTheme, setDarkLightTheme } = props;
    const [activeTab, setActiveTab] = useState<number>(0);
    const [activeItem, setActiveItem] = useState<string>("");
    const [currentUser, setCurrentUser] = useState(null);
    const { pathname } = useLocation();
    const { user, logout } = useAuth();

    // Inicializar currentUser cuando user cambie
    React.useEffect(() => {
        if (user) {
            setCurrentUser(user);
        }
    }, [user]);

    // Función para actualizar el usuario local
    const handleUserUpdate = (updatedUser) => {
        setCurrentUser(updatedUser);
    };

    const onLogout = () => {
        logout();
    };

    const isCurrentPath = (path) => {
        if (pathname === '/student') return false;
        if (path === pathname) return true;
        if (Array.isArray(path)) {
            return path.some(p =>
                typeof p === 'string' ? pathname.includes(p) : p.test(pathname)
            );
        } else {
            return typeof path === 'string' ? pathname.includes(path) : path.test(pathname);
        }
    };

    const handleClick = (itemName: string) => {
        setActiveItem(itemName);
    };

    const handleTabClicked = (index: number) => setActiveTab(index);

    const getLogo = darkLightTheme
        ? "url('/img/logo/logo_white.png')"
        : "url('/img/logo/logo.png')";

    return (
        <aside className={`student-menu ${sidebarOpen ? "open" : ""}`}>
            <header className="sidebar-student-header">
                <div
                    className={`sidebar-student-header__logo`} style={{
                        backgroundImage: getLogo,
                        backgroundSize: "contain",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "center",
                        transition: "background-image 0.5s",
                    }}
                />
            </header>
            <Nav activeTab={activeTab} onTabClicked={handleTabClicked} />
            <ReactCarousel
                className="react-carousel"
                showArrows={false}
                showStatus={false}
                showThumbs={false}
                showIndicators={false}
                swipeable={true}
                emulateTouch={true}
                selectedItem={activeTab}
                onChange={handleTabClicked}
            >
                <nav className="sidebar-student-menu">
                    {navItems.map(item => (
                        <NavButton
                            key={item.name}
                            onClick={handleClick}
                            name={item.name}
                            icon={item.icon}
                            to={item.to}
                            isActive={isCurrentPath(item.path)}
                        />
                    ))}
                </nav>
                <div className="sidebar-student-form" >
                    <div className="sidebar-user-form-scroll">
                        <UserSidebarForm user={currentUser || user} onUserUpdate={handleUserUpdate} />
                    </div>
                    <PasswordUpdateForm />
                </div>
                <div className="sidebar-settings-form" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <SettingsSidebarForm darkLightTheme={darkLightTheme} setDarkLightTheme={setDarkLightTheme} />
                    <Divider className="divider-menu" style={{ margin: 0 }} />
                    <Button
                        className="logout-button"
                        color="red"
                        icon
                        labelPosition="left"
                        onClick={onLogout}
                    >
                        <Icon name="sign-out" /> Cerrar sesión
                    </Button>
                </div>
            </ReactCarousel>
        </aside>
    );
};